import React, { useContext, useEffect, useState } from 'react';
import { Users, Plus, Mail, Shield, MoreHorizontal, SwitchCamera, ChevronDown, Trash } from 'lucide-react';
import { Button } from '../ui/button';
import { teamMembers, roles, pendingInvites } from '../../data/team';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { AuthContext } from '@/contexts/AuthContext';
import { TeamsApi, PaginatedInvitationList, RoleEnum, Invitation, Membership } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import { useTeams } from '@/contexts/TeamsContext';
import { toast } from 'sonner';
import { TeamMembersActionsMenu } from './TeamMembersActionsMenu';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { SelectMenu } from '../ui/SelectMenu';

const ROLES: any = [
  { id: RoleEnum.Member, label: 'Member' },
  { id: RoleEnum.Admin, label: 'Administrator' },
];

export const TeamSettings = () => {
  const { token } = useContext(AuthContext);
  const { currentTeam, removeMember } = useTeams();
  const { user } = useContext(AuthContext);
  const client = new TeamsApi(getApiConfiguration(token));
  const confirmDialog = useConfirmDialog();
  const [invitations, setInvitations] = useState<PaginatedInvitationList>({ count: 0, next: null, previous: null, results: [] });
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleEnum>(RoleEnum.Member);
  const [loadInvitations, setLoadInvitations] = useState(false);

  const handleInviteMember = async () => {
    // TODO: Implement the API call to invite member
    if (!currentTeam?.slug) return;
    console.log("invite user")
    if (!inviteEmail || !selectedRole || !user?.getDisplayName) return;
    await client.invitationsCreate({teamSlug: currentTeam?.slug, invitation: {email: inviteEmail, role: selectedRole, teamName: currentTeam.name, invitedBy: user?.getDisplayName}}).catch((e) => console.log(e));
    setIsInviteDialogOpen(false);
    setInviteEmail('');
    setSelectedRole(RoleEnum.Member);
    setLoadInvitations(true);
    toast.success("Invitation sent successfully")
  };

  const handleDeleteInvite = async (inviteId: string) => {
    if (!currentTeam?.slug) return;
    await client.invitationsDestroy({ id: inviteId, teamSlug: currentTeam?.slug })
    setLoadInvitations(true)
    toast.success("Invitation deleted successfully")
  }

  const handleResendInvite = async (invitation: Invitation) => {

    if (!currentTeam?.slug || !user?.getDisplayName || !invitation) return;
    await client.invitationsResend({ id: invitation.id, teamSlug: currentTeam?.slug, invitation }).then((d) => console.log(d));
    toast.success("Invitation resent successfully")
  }

  const handleChangeRole = async (memberId: number, memberRole: RoleEnum, memeber: Membership) => {
    console.log(currentTeam?.slug, memberRole)
    if (!currentTeam?.slug) return;
    await client.membershipsUpdate({
      id: memberId?.toString(), teamSlug: currentTeam?.slug, membership: {...memeber, role: memberRole}
    }).then((d) => console.log(d)).catch((e) => console.log(e));
    setLoadInvitations(true)
    toast.success("Role changed successfully")
  }

  const handleDeleteMember = async (memberId: number) => {
    if (!currentTeam?.slug) return;
    await removeMember(currentTeam.slug, memberId?.toString());
    toast.success("Member removed successfully")
  }

  useEffect(() => {
    const fetchInvitations = async () => {
      if (currentTeam?.slug) {
        try {
          const response = await client.invitationsList({ teamSlug: currentTeam?.slug });
          setInvitations({...response, count: response.results.filter((i: Invitation) => !i.isAccepted)?.length, results: response.results.filter((i: Invitation) => !i.isAccepted)});
          setLoadInvitations(false);
        } catch (error) {
          console.error('Error fetching team members:', error);
        }
      }
    };

    fetchInvitations();
  }, [currentTeam?.slug, loadInvitations]);
  return (
    <div className="space-y-6">
      {/* Team Switcher */}
      {/* <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <SwitchCamera className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Team Selection</h2>
          </div>
          <Link to="../team-switcher">
            <Button>
              Switch Team
            </Button>
          </Link>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Switch between different teams or manage your team access.
        </p>
      </div> */}

      {/* Team Members */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Team Members</h2>
          </div>
          <Button onClick={() => setIsInviteDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>

          <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Add a new member to your team by sending them an invitation email.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-200" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-200" htmlFor="role">
                    Role
                  </label>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as RoleEnum)}
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={RoleEnum.Member}>Member</option>
                    <option value={RoleEnum.Admin}>Administrator</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsInviteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteMember}
                  disabled={!inviteEmail || !selectedRole}
                >
                  Send Invitation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-6">
          {/* Team Members List */}
          <div className="space-y-4">
            {currentTeam?.members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group">
                <div className="flex items-center space-x-4">
                  <img
                    src={member.avatarUrl}
                    alt={member.firstName}
                    className="h-10 w-10 rounded-full"
                  />
                  <div>
                    <div className="text-sm font-medium text-white">{member.firstName} {member.lastName}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <Mail className="h-3 w-3 text-gray-500" />
                      <span className="text-sm text-gray-400">{member.email}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <SelectMenu
                      options={ROLES}
                      selectedId={member.role}
                      onSelect={(roleId) => handleChangeRole(member.id, roleId as RoleEnum, member)}
                      defaultIcon={<Shield className="h-3 w-3 text-gray-500" />}
                      label={"Select Role"}
                      placeholder={member.role}
                      disabled={user?.id === member.userId && currentTeam?.isAdmin}
                    />
                    {/* <div className="flex items-center space-x-2">
                      <Shield className="h-3 w-3 text-gray-500" />
                      <span className="text-sm text-gray-300">{member.role}</span>
                    </div> */}
                    {/* <div className="text-xs text-gray-500 mt-1">
                      Active {member.lastActive}
                    </div> */}
                  </div>
                  {(user?.id !== member.userId && currentTeam?.isAdmin) && (
                    <button
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-800/50 transition-colors"
                      onClick={async () => {
                        const confirmed = await confirmDialog.confirm({
                          title: `Delete member ${member.id}?`,
                          description: 'This action is permanent.',
                          confirmLabel: 'Delete',
                          variant: 'destructive'
                        });
                        if (confirmed) {
                          await handleDeleteMember(member.id);
                        }
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                    // <TeamMembersActionsMenu
                    //   member={member}
                    //   onRoleChange={handleChangeRole}
                    //   onDelete={handleDeleteMember}
                    // />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Roles and Permissions */}
          <div className="pt-6 border-t border-gray-800">
            <h3 className="text-sm font-medium text-white mb-4">Roles and Permissions</h3>
            <div className="space-y-4">
              {roles.map((role) => (
                <div key={role.name} className="p-4 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-medium text-white">{role.name}</div>
                      <div className="text-sm text-gray-400">{role.description}</div>
                    </div>
                    {/* <Button variant="outline" size="sm">Edit Role</Button> */}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {role.permissions.map((permission) => (
                      <div
                        key={permission}
                        className="flex items-center space-x-2 px-2 py-1 rounded bg-gray-800/50"
                      >
                        <Shield className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-300">{permission}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Invites */}
          <div className="pt-6 border-t border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white">Pending Invites</h3>
              <span className="text-xs text-gray-500">{invitations.count} pending</span>
            </div>
            <div className="space-y-3">
              {invitations.results.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm text-white">{invite.email}</div>
                      {/* <div className="text-xs text-gray-500">Invited {invite.invitedAt}</div> */}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResendInvite(invite)}
                    >Resend</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-400"
                      onClick={async () => {
                        const confirmed = await confirmDialog.confirm({
                          title: `Delete Invitation for ${invite.email}?`,
                          description: 'This action is permanent.',
                          confirmLabel: 'Confirm',
                          variant: 'destructive'
                        });
                        if (confirmed) {
                          await handleDeleteInvite(invite.id);
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
      />
    </div>
  );
};